import { getPlayersByPlayerExternalIdGamesDataOptions } from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";

import { ChartType, GameType, PitcherHand, PlayerType } from "@/lib/types";

/**
 * Parameters for fetching player chart data from the API.
 *
 * These parameters are used to construct API requests for various types of
 * baseball analytics charts including heat maps, spray charts, and more.
 */
export interface UseChartDataParams {
  /** Unique identifier for the player in the external system */
  playerExternalId: string;
  /** Start date for the data range (ISO date string) */
  startDate: string;
  /** End date for the data range (ISO date string) */
  endDate: string;
  /** Type of game data to fetch */
  gameType: GameType;
  /** Whether to fetch pitcher or hitter data */
  playerType: PlayerType;
  /** Handedness of opposing pitcher (for hitter data) or batter (for pitcher data) */
  vsHandedness: PitcherHand;
  /** Type of chart visualization to generate */
  chartType: ChartType;
  /** Optional filter by result type */
  resultType?: "out" | "hit" | "whiff" | "contact";
  /** Optional filter by pitch type category */
  pitchType?: string;
  /** Additional filters to apply to the data query */
  filters?: Record<string, unknown>;
  /** Optional grouping parameters for data aggregation */
  groupBy?: string[];
  /** Optional filter by events that are either a hit or an out*/
  hitsAndOutsOnly?: boolean;
}

const vsHandednessMap: Record<PitcherHand, "left" | "right" | undefined> = {
  all: undefined,
  righty: "right",
  lefty: "left",
};

/**
 * Custom React hook for fetching chart data from the API.
 *
 * This hook uses React Query to manage the API request lifecycle including
 * caching, loading states, and error handling. It transforms the parameters
 * to match the backend API format and handles conditional requests.
 *
 * @param params - Configuration parameters for the chart data request
 * @returns Object containing data, loading state, and error information
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = useChartData({
 *   playerExternalId: "player-123",
 *   startDate: "2024-01-01",
 *   endDate: "2024-12-31",
 *   gameType: "game",
 *   playerType: "pitcher",
 *   vsHandedness: "all",
 *   chartType: "heatmap",
 *   filters: { is_fastball: "eq.true" },
 *   hitsAndOutsOnly: true
 * });
 * ```
 */
export const useChartData = (params: UseChartDataParams) => {
  const { playerExternalId, startDate, endDate, chartType } = params;

  const { data, isLoading, error } = useQuery({
    ...getPlayersByPlayerExternalIdGamesDataOptions({
      path: {
        player_external_id: encodeURIComponent(params.playerExternalId),
      },
      query: {
        startDate: params.startDate,
        endDate: params.endDate,
        gameType:
          params.gameType === "practice"
            ? params.playerType === "pitcher"
              ? "bullpen"
              : "batting_practice"
            : "game",
        playerType: params.playerType,
        ...(params.vsHandedness !== "all" && {
          vsHandedness: vsHandednessMap[params.vsHandedness],
        }),
        chartType: params.chartType,
        ...(params.hitsAndOutsOnly && { hitsAndOutsOnly: true }),
        ...(params.resultType && { resultType: params.resultType }),
        ...(params.pitchType && { pitchType: params.pitchType }),
        ...(params.filters && { filters: JSON.stringify(params.filters) }),
        ...(params.groupBy && { groupBy: JSON.stringify(params.groupBy) }),
        ...(params.hitsAndOutsOnly && { hitsAndOutsOnly: true }),
      },
    }),
    enabled: !!playerExternalId && !!startDate && !!endDate && !!chartType,
    retry: false,
  });

  return {
    data,
    isLoading,
    error,
  };
};
